import { Injectable } from "@angular/core";
import { CoreHttpClient } from "@obg/ngx.core";
import { concat, deburr, filter, find, flow, isNumber, map as lodashMap, sortBy, sortedUniq } from "lodash-es";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { CallingCode } from "../calling-code/calling-code.model";
import { CommonContentItem } from "../common-content/common-content.model";
import { CommonContentClient } from "../content/common-content.client";

import { CountryConfigService } from "./country.config";
import {
	AdministrativeArea,
	Country,
	CountryOrderType,
	CountryResponse,
	GetCountriesResponse,
	GetCountriesResponseRaw,
	State,
	StateCity,
} from "./country.model";

@Injectable({
	providedIn: "root",
})
/**
 * @deprecated [fabric-deprecated] - use mfe svc-content CountryService instead
 */
export class CountryService {
	constructor(
		private httpClient: CoreHttpClient,
		private config: CountryConfigService,
		private commonContentClient: CommonContentClient
	) {}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getTransformedCountries)" instead
	 */
	getCountries(): Observable<GetCountriesResponse> {
		return this.httpClient.get<GetCountriesResponseRaw>({ url: "i18n/v1/countries" }).pipe(
			map((response) => {
				const mappedCountries = lodashMap(response.countries, this.processCountry);
				return {
					countries: mappedCountries,
					currentCountry: response.currentCountryCode
						? this.processCountry(response.currentCountryCode)
						: this.getByCode(mappedCountries, this.config.getDefaultCountryCode()),
				};
			})
		);
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getBirthCountries)" instead
	 */
	getBirthCountries(): Observable<GetCountriesResponse> {
		return this.httpClient.get<GetCountriesResponseRaw>({ url: "i18n/v1/countries/birth-countries" }).pipe(
			map((response) => {
				const mappedCountries = lodashMap(response.countries, this.processCountry);
				return {
					countries: mappedCountries,
					currentCountry: response.currentCountryCode
						? this.processCountry(response.currentCountryCode)
						: this.getByCode(mappedCountries, this.config.getDefaultCountryCode()),
				};
			})
		);
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCountriesByOrder)" instead
	 */
	getOrderedCountries(countries: Country[], currentCountry?: Country, order = CountryOrderType.None): Country[] {
		return sortBy(countries, this.getCountrySorter(order, currentCountry));
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getStateCodeFromCityName)" instead
	 */
	getStateCodeFromCityName(states: State[] | undefined, cityName: string): string | undefined {
		const provinceCode = cityName.trim().split(",").pop()?.trim();
		return states?.find((state) => state.code === provinceCode)?.code;
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCombinedCities)" instead
	 */
	getCombinedCities(states: State[] | undefined, blockedProvinces: string[] = []): StateCity[] | undefined {
		return states
			?.filter((province) => !blockedProvinces.includes(province.code.toUpperCase()))
			.map((province) => province.cities)
			.reduce((acc, curVal) => acc!.concat(curVal!), []);
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCombinedCountriesByOrder)" instead
	 */
	getCombinedCountries(
		countries: Country[],
		currentCountry?: Country,
		callingCodes?: CallingCode[],
		order = CountryOrderType.None
	): Country[] {
		const orderedCountries = this.getOrderedCountries(countries, currentCountry, CountryOrderType.Alphabetically);
		const countryCodesPriority = this.config.getCountryCodesPriority();
		return flow(
			(countryList: Country[]) =>
				filter(
					countryList,
					(country) =>
						this.isCurrentCountry(country, currentCountry) || isNumber(countryCodesPriority[country.code])
				),
			(countryList) => sortBy(countryList, this.getCountrySorter(order, currentCountry)),
			(countryList) => concat(countryList, orderedCountries),
			(countryList) => [...new Set(countryList)],
			(countryList) =>
				countryList.map((country) => ({
					code: country.code,
					name: country.name,
					callingCode:
						country.callingCode ??
						callingCodes?.find((callingCode) => callingCode?.countryCode === country.code)
							?.countryCallingCode,
				}))
		)(orderedCountries);
	}

	/**
	 * Returns a country by its code (e.g.: "MT", "UK", "SE").
	 */
	getByCode(countries: Country[], countryCode: string): Country | undefined {
		return find(countries, (country) => country.code === countryCode);
	}

	/**
	 * Returns a CallingCode  (e.g. {countryCode: 'AI', countryCallingCode: '1'}) by its code (e.g.: "AI", "UK", "SE").
	 */
	getByCountryCode(callingCodes: CallingCode[], countryCode: string): CallingCode | undefined {
		return find(callingCodes, (callingCode) => callingCode.countryCode === countryCode);
	}

	/**
	 * Returns countries by their code (e.g.: ["MT", "UK", "SE"]).
	 */
	getByCodes(countries: Country[], countryCodes: string[]): Country[] {
		const matchingCountries: Country[] = [];
		for (const country of countries) {
			if (countryCodes.indexOf(country.code) > -1) {
				matchingCountries.push(country);
			}
			if (matchingCountries.length === countryCodes.length) {
				break;
			}
		}
		return matchingCountries;
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCountryStates)" instead
	 */
	getCountryStates(countrySelection: string | undefined, countryCode: string | undefined): Observable<object> {
		return this.httpClient.get({
			url: countrySelection ? `i18n/v2/countries/${countrySelection}/states` : this.config.getStatesEndpoint(),
			searchParams: { countryCode },
		});
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCountryStatesFromContent)" instead
	 */
	getCountryStatesFromContent(jurisdiction: string): Observable<State[]> {
		return this.commonContentClient
			.getCommonContent("registrationstates", {
				isLoggedIn: false,
				jurisdiction: jurisdiction,
			})
			.pipe(
				map((states: CommonContentItem[]) => {
					const provinces: State[] = states.map((state: CommonContentItem) => {
						const stateName = state.content?.name?.[0] || "";
						return { name: stateName, code: stateName };
					});
					return provinces;
				})
			);
	}

	/**
	 *
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCountryCallingCodes)" instead
	 *
	 * Returns the distinct country calling codes sorted, and putting the calling code of the current country at the first place.
	 * e.g.: in case Malta is the current country:  ["356", "1", "27", "36", "248"]
	 */
	getCallingCodes(countries: Country[], currentCountry?: Country): string[] {
		const countryCallingCodes = flow(
			(countryList: Country[]) => lodashMap(countryList, (country) => country.callingCode),
			// sort numerically (e.g.: 35 is before 276)
			(countryList) => sortBy(countryList, (callingCode) => parseInt(callingCode, 10)),
			// countries may have the same calling codes (e.g.: Antigua and Barbuda and Bahamas are both 1)
			sortedUniq
		)(countries);

		return this.addCurrentCallingCodeFirst(currentCountry, countryCallingCodes);
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getAdministrativeAreasByCountryCode)" instead
	 */
	getAdministrativeAreas(countryCode: string): Observable<AdministrativeArea> {
		return this.httpClient.get<any>({
			url: `i18n/v4/countries/${countryCode}`,
		});
	}

	private addCurrentCallingCodeFirst(currentCountry: Country | undefined, callingCodes: string[]): string[] {
		if (!currentCountry) {
			return callingCodes;
		}

		return [currentCountry.callingCode, ...callingCodes.filter((code) => code !== currentCountry.callingCode)];
	}

	private getAlphabeticalCountrySorter() {
		return (country: Country): string => deburr(country.name);
	}

	private getPriorityCountrySorter(currentCountry?: Country): (country: Country) => string {
		const countryCodesPriority = this.config.getCountryCodesPriority();
		return (country: Country): string => {
			if (this.isCurrentCountry(country, currentCountry)) {
				return "-1";
			}

			const priority = countryCodesPriority[country.code];
			if (isNumber(priority)) {
				// need to return string because numbers are coming after letters
				return priority.toString();
			}

			return country.name;
		};
	}

	private getCountrySorter(order: CountryOrderType, currentCountry?: Country): (country: Country) => string {
		switch (order) {
			case CountryOrderType.Alphabetically: {
				return this.getAlphabeticalCountrySorter();
			}
			case CountryOrderType.ByPriority:
			default: {
				return this.getPriorityCountrySorter(currentCountry);
			}
		}
	}

	private isCurrentCountry(country: Country, currentCountry?: Country): boolean {
		return country.code === currentCountry?.code;
	}

	private processCountry(country: CountryResponse): Country {
		return {
			name: country.countryName,
			code: country.countryCode,
			callingCode: country.countryCallingCode,
		};
	}
}
